import { Alert, Button, Group, Stack, Text } from '@mantine/core';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import { InterventionResponseContract, InterventionResponseField, TransformDataStep } from '@/types/automationSteps.ts';

type Props = { step: TransformDataStep };
type Fields = { intervention_response?: InterventionResponseContract | null };

const FIELD_TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'enum', label: 'Select from choices' },
  { value: 'year_month', label: 'Year and month (YYYY-MM)' },
];

const defaultField = (): InterventionResponseField => ({
  key: 'reviewer_response',
  label: 'Reviewer response',
  description: '',
  field_type: 'text',
  choices: [],
});

const defaultContract = (): InterventionResponseContract => ({
  binding_name: 'intervention_response',
  fields: [defaultField()],
});

const InterventionResponseFields = ({ step }: Props) => {
  const { fields, onChange, onChangeNoDelay } = useUpdateStepFields<Fields>({ step, syncWithAPI: true });
  const contract = fields.intervention_response ?? null;

  const updateContractImmediately = (next: InterventionResponseContract | null) =>
    onChangeNoDelay({ intervention_response: next });
  const updateContractText = (next: InterventionResponseContract) => onChange({ intervention_response: next });

  if (!contract) {
    return (
      <Stack gap="xs">
        <Text fw={500}>Structured intervention</Text>
        <Text size="sm" c="dimmed">
          Let an eligible paused provided-code transform request a small typed reviewer response and continue safely.
        </Text>
        <Button size="xs" variant="light" onClick={() => updateContractImmediately(defaultContract())}>
          Configure structured intervention
        </Button>
      </Stack>
    );
  }

  const updateField = (index: number, patch: Partial<InterventionResponseField>) => {
    const nextFields = contract.fields.map((field, fieldIndex) =>
      fieldIndex === index ? { ...field, ...patch } : field,
    );
    updateContractImmediately({ ...contract, fields: nextFields });
  };

  const updateTextField = (index: number, patch: Partial<InterventionResponseField>) => {
    const nextFields = contract.fields.map((field, fieldIndex) =>
      fieldIndex === index ? { ...field, ...patch } : field,
    );
    updateContractText({ ...contract, fields: nextFields });
  };

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text fw={500}>Structured intervention</Text>
        <Button size="xs" color="red" variant="subtle" onClick={() => updateContractImmediately(null)}>
          Disable
        </Button>
      </Group>
      <Alert color="blue" title="Safe continuation only">
        This is available only for root-level provided-code transforms. It does not accept generic feedback or rerun
        upstream work.
      </Alert>
      <StyledTextInput
        label="Code input binding"
        value={contract.binding_name}
        onChange={(event) => updateContractText({ ...contract, binding_name: event.currentTarget.value })}
      />
      {contract.fields.map((field, index) => (
        <Stack key={`intervention-field-${index}`} gap="xs" p="xs" style={{ border: '1px solid var(--mantine-color-gray-3)' }}>
          <StyledTextInput
            label="Field key"
            value={field.key}
            onChange={(event) => updateTextField(index, { key: event.currentTarget.value })}
          />
          <StyledTextInput
            label="Reviewer label"
            value={field.label}
            onChange={(event) => updateTextField(index, { label: event.currentTarget.value })}
          />
          <StyledTextInput
            label="Reviewer guidance"
            value={field.description}
            onChange={(event) => updateTextField(index, { description: event.currentTarget.value })}
          />
          <StyledSelect
            label="Response type"
            data={FIELD_TYPE_OPTIONS}
            value={field.field_type}
            onChange={(value) =>
              updateField(index, {
                field_type: value as InterventionResponseField['field_type'],
                choices: value === 'enum' ? (field.choices.length ? field.choices : ['Option 1']) : [],
              })
            }
            allowDeselect={false}
            clearable={false}
          />
          {field.field_type === 'enum' && (
            <StyledTextInput
              label="Allowed choices"
              description="Comma-separated exact values. Replace the default choice before publishing."
              value={field.choices.join(', ')}
              onChange={(event) => {
                const choices = event.currentTarget.value.split(',').map((item) => item.trim()).filter(Boolean);
                updateTextField(index, { choices: choices.length ? choices : ['Option 1'] });
              }}
            />
          )}
          {contract.fields.length > 1 && (
            <Button size="xs" variant="subtle" color="red" onClick={() => updateContractImmediately({ ...contract, fields: contract.fields.filter((_, fieldIndex) => fieldIndex !== index) })}>
              Remove field
            </Button>
          )}
        </Stack>
      ))}
      {contract.fields.length < 5 && (
        <Button size="xs" variant="light" onClick={() => updateContractImmediately({ ...contract, fields: [...contract.fields, defaultField()] })}>
          Add response field
        </Button>
      )}
    </Stack>
  );
};

export default InterventionResponseFields;
